import { useEffect, useState, useRef } from 'react';
import axios from '../../../../helpers/axios';
import { logError } from '../../../../services';
import { isSafari } from 'react-device-detect';
import { useDropzone } from 'react-dropzone';
import { useParams, useHistory } from 'react-router-dom';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { FileDrop, Button as SMButton, Loader, SMBSelect } from '../../../../components';
import CanvasDraw from '../../../../components/canvas';
import { ButtonGroup, Typography, Button, FormControl, MenuItem, Box } from '@mui/material';
import {
  Save,
  Close,
  TextFields,
  Create,
  Undo,
  Delete,
  Done,
  Add,
  Remove
} from '@mui/icons-material';
import { combineDrawing } from '../../../../helpers/canvas';
import { CirclePicker } from 'react-color';
// fetch
import { createPlatMap, createMarkedPlatMap, getPlatMaps } from '../../../../fetch/plat-maps';
// models
import { IPlatMap } from '../../../../models';
import { imageFileTypes } from '../../../../components/file/utils';

export const PlatMap = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [image, setImage] = useState<string>('');
  const [platMaps, setPlatMaps] = useState<IPlatMap[]>([]);
  const [originalImageFile, setOriginalImageFile] = useState<any>(null);
  const [isDisabled, setDisabled] = useState<boolean>(true);
  const [isActive, setActive] = useState<boolean>(false);
  const [isPencilActive, setPencilActive] = useState<boolean>(false);
  const [isTextActive, setTextActive] = useState<boolean>(false);
  const [hasInput, setHasInput] = useState<boolean>(false);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [isLoadingPlatMaps, setLoadingPlatMaps] = useState<boolean>(false);
  const [selectedPlatMap, setSelectedPlatMap] = useState<string>('');
  const [isLoadingImage, setLoadingImage] = useState<boolean>(false);
  const [color, setColor] = useState('#000000');

  const colorOptions = ['#000000', '#FFFFFF', '#749F3E', '#E7BD18', '#D61707', '#156FA0'];
  const [zoomScale, setZoomScale] = useState<number>(1);

  const { neighborhoodId }: { neighborhoodId: string } = useParams();

  const canvasRef = useRef(null);

  const history = useHistory();

  const classes = useStyles();

  const imageDZUrl = useDropzone({
    accept: {
      'image/*': []
    },
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        const acceptedFile = acceptedFiles[0];
        const imageUrl = URL.createObjectURL(acceptedFile);
        const img = document.createElement('img');
        img.src = imageUrl;
        img.onload = () => {
          handlePlatMapChange('');
          setImage(imageUrl);
          setOriginalImageFile(acceptedFiles[0]);
        };
      } else {
        enqueueSnackbar(
          `Invalid file type. Please upload one of these image types, ${imageFileTypes.join(
            ', '
          )}.`,
          {
            variant: 'error'
          }
        );
      }
    }
  });

  const fetchPlatMaps = async () => {
    try {
      setLoadingPlatMaps(true);
      const res = await getPlatMaps(neighborhoodId);
      setPlatMaps(res);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingPlatMaps(false);
    }
  };

  const fetchImage = async (value: string) => {
    setLoadingImage(true);
    try {
      const success = await axios.get(
        `/api/neighborhoods/${neighborhoodId}/plat-maps/${value}?Marked=True`
      );
      if (success) {
        setSelectedPlatMap(value);
        setImage(`/api/neighborhoods/${neighborhoodId}/plat-maps/${value}?Marked=True`);
        setLoadingImage(false);
      }
    } catch (error) {
      try {
        const success = await axios.get(`/api/neighborhoods/${neighborhoodId}/plat-maps/${value}`);
        if (success) {
          setSelectedPlatMap(value);
          setImage(`/api/neighborhoods/${neighborhoodId}/plat-maps/${value}`);
          setLoadingImage(false);
        }
      } catch (error) {
        logError(error, 'fetchImage failed');
        setSelectedPlatMap('');
        setLoadingImage(false);
        enqueueSnackbar('Error loading plat map, please refresh and try again.', {
          variant: 'error'
        });
      }
    }
  };

  useEffect(() => {
    if (neighborhoodId !== 'add') {
      fetchPlatMaps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoodId]);

  const handlePlatMapChange = async (value: string, shouldSetImage?: boolean) => {
    if (!shouldSetImage) {
      setSelectedPlatMap(value);
    }
    if (shouldSetImage) {
      setImage('');
      // reset on plat map change
      setZoomScale(1);
      fetchImage(value);
    }
  };

  const handleTextAdd = (e: any) => {
    if (hasInput) {
      return;
    }
    if (canvasRef && canvasRef.current) {
      const { x, y } = (canvasRef.current as any).getPointerPos(e);
      addInput(x * zoomScale, y * zoomScale);
    }
  };

  // Function to dynamically add an input box:
  const addInput = (x: number, y: number) => {
    let textarea = document.createElement('textarea');

    textarea.className = 'image-textarea';
    textarea.style.color = `${color}`;
    textarea.style.position = 'absolute';
    textarea.style.left = `${x}px`;
    textarea.style.top = `${y}px`;
    textarea.style.zIndex = '10';
    textarea.style.padding = '5px';
    textarea.style.fontSize = '14px';
    textarea.style.resize = 'vertical';

    textarea.onkeydown = handleEnter;
    textarea.onblur = handleCancel;
    const canvasWrapper = document.getElementById('canvas-wrapper');
    if (canvasWrapper) {
      canvasWrapper.appendChild(textarea);
    }

    textarea.focus();

    setHasInput(true);
  };

  const handleCancel = (e: any) => {
    const canvasWrapper = document.getElementById('canvas-wrapper');
    if (canvasWrapper) {
      canvasWrapper.removeChild(e.target);
      setHasInput(false);
    }
  };

  // Key handler for input box:
  const handleEnter = (e: any) => {
    const keyCode = e.keyCode;
    const canvasWrapper = document.getElementById('canvas-wrapper');
    if (keyCode === 27) {
      if (canvasWrapper) {
        e.target.blur();
        setHasInput(false);
      }
      // disregard input
    } else if (keyCode === 13) {
      const left = parseInt(e.target.style.left, 10) / zoomScale;
      const top = parseInt(e.target.style.top, 10) / zoomScale;
      drawText(e.target.value, left, top);
      if (canvasWrapper) {
        e.target.blur();
        setHasInput(false);
      }
    }
  };

  // Draw the text onto canvas:
  function drawText(txt: string, x: number, y: number) {
    if (canvasRef && canvasRef.current) {
      (canvasRef.current as any).ctx.interface.textBaseline = 'top';
      (canvasRef.current as any).ctx.interface.textAlign = 'left';
      (canvasRef.current as any).ctx.interface.font = '18px sans-serif';
      (canvasRef.current as any).ctx.interface.fillStyle = color;
      (canvasRef.current as any).ctx.interface.fillText(txt, x - 4, y - 4);
    }
  }
  return (
    <Box mt={1}>
      {neighborhoodId !== 'add' && (
        <div className={classes.selectWrapper}>
          <div className={classes.select}>
            <FormControl fullWidth variant="outlined" size="small">
              <SMBSelect
                name="plat-maps"
                label="Select Plat Map"
                id="plat-maps"
                variant="outlined"
                disabled={isLoadingPlatMaps}
                value={selectedPlatMap}
                handleClear={() => {
                  setSelectedPlatMap('');
                  setImage('');
                }}
                onChange={({ target: { value } }) => {
                  handlePlatMapChange(value as string, true);
                }}
              >
                {platMaps.map((map, index) => {
                  return (
                    <MenuItem key={`${index}`} value={map.platMapId}>
                      {map.displayName || map.platMapId}
                    </MenuItem>
                  );
                })}
              </SMBSelect>
            </FormControl>
          </div>
        </div>
      )}
      {isLoadingImage && (
        <Box mt={1} mb={1}>
          <Loader position="centered" type="inline" />
        </Box>
      )}

      {!image && !isLoadingImage && (
        <FileDrop
          id="plat-map-image-drop"
          {...imageDZUrl}
          title="Add Plat Map"
          className={classes.logoDrop}
        >
          <Typography variant="body1">
            Drag and drop a PNG or JPG file or{' '}
            <Box
              component="a"
              href="/#"
              sx={{ color: theme => theme.palette.primary.main, textDecoration: 'none' }}
              onClick={e => e.preventDefault()}
            >
              browse and pick
            </Box>{' '}
            a file.
          </Typography>
          <Typography variant="body2">Recommended image size of 1000x1000</Typography>
        </FileDrop>
      )}
      {image && (
        <>
          <div className={classes.editButtonsWrapper}>
            <ButtonGroup className={classes.editButtonGroup}>
              <Button
                type="button"
                startIcon={isPencilActive ? <Done /> : <Create />}
                variant="contained"
                color="secondary"
                disabled={isTextActive || isSaving}
                size="small"
                onClick={() => {
                  setDisabled(!isDisabled);
                  setPencilActive(!isPencilActive);
                  setColor(colorOptions[0]);
                }}
              >
                {isPencilActive ? 'Done' : 'Pencil'}
              </Button>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                size="small"
                disabled={isPencilActive || isSaving}
                startIcon={isTextActive ? <Done /> : <TextFields />}
                onClick={() => {
                  setTextActive(!isTextActive);
                  setColor(colorOptions[0]);
                }}
              >
                {isTextActive ? 'Done' : 'Text'}
              </Button>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                size="small"
                disabled={isSaving}
                startIcon={<Add />}
                onClick={() => {
                  let scale = zoomScale;
                  const scaleMultiplier = 0.8;
                  setZoomScale((scale /= scaleMultiplier));
                }}
              >
                Zoom In
              </Button>
              <Button
                type="button"
                variant="contained"
                color="secondary"
                size="small"
                disabled={isSaving}
                startIcon={<Remove />}
                onClick={() => {
                  let scale = zoomScale;
                  const scaleMultiplier = 0.8;
                  setZoomScale((scale *= scaleMultiplier));
                }}
              >
                Zoom Out
              </Button>
            </ButtonGroup>

            {isPencilActive && (
              <ButtonGroup>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  disabled={!isActive || isSaving}
                  startIcon={<Close />}
                  onClick={() => {
                    if (canvasRef && canvasRef.current) {
                      setActive(false);
                      (canvasRef.current as any).clear();
                    }
                  }}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  size="small"
                  disabled={!isActive || isSaving}
                  startIcon={<Undo />}
                  onClick={() => {
                    if (canvasRef && canvasRef.current) {
                      setActive(false);
                      (canvasRef.current as any).undo();
                    }
                  }}
                >
                  Undo
                </Button>
              </ButtonGroup>
            )}
          </div>
          {(isPencilActive || isTextActive) && (
            <div className={classes.optionsButtonsWrapper}>
              <div className={classes.optionsGroup}>
                <Typography className={classes.colorLabel} variant="body1">
                  Pick a color:{' '}
                </Typography>
                <CirclePicker
                  color={color}
                  onChangeComplete={(color, event) => {
                    setColor(color.hex);
                  }}
                  colors={colorOptions}
                />
              </div>
            </div>
          )}
          <div
            key={isSafari ? zoomScale : undefined}
            style={{
              position: 'relative',
              cursor: isPencilActive ? 'crosshair' : isTextActive ? 'text' : 'default',
              overflow: 'scroll'
            }}
            id="canvas-wrapper"
            onClick={e => {
              if (isTextActive) {
                handleTextAdd(e);
              }
            }}
          >
            <CanvasDraw
              onChange={e => {
                setActive(true);
              }}
              hideGrid
              brushRadius={0}
              ref={canvasRef}
              brushColor={`${color}`}
              canvasWidth={1000}
              canvasHeight={1000}
              disabled={isDisabled}
              imgSrc={image}
              style={{
                transform: `scale(${zoomScale}, ${zoomScale})`,
                transformOrigin: '0% 0%'
              }}
            />
          </div>
          <div className={classes.removeWrapper}>
            <Button
              className={classes.removeButton}
              onClick={() => {
                setZoomScale(1);
                setImage('');
                setTextActive(false);
                setPencilActive(false);
                setDisabled(true);
                handlePlatMapChange('');
              }}
              startIcon={<Delete />}
              disabled={isSaving}
            >
              Remove Plat Map
            </Button>
          </div>
        </>
      )}
      <ButtonGroup className={classes.buttonGroup}>
        <SMButton
          type="button"
          id="save-button"
          startIcon={<Save />}
          variant="contained"
          disabled={!image}
          color="primary"
          loading={isSaving ? 'Saving...' : ''}
          onClick={async () => {
            setTextActive(false);
            setPencilActive(false);
            if (canvasRef && canvasRef.current) {
              const currentPlatMap = platMaps.find(plat => plat.platMapId === selectedPlatMap);
              const { blob } = combineDrawing(canvasRef, currentPlatMap, originalImageFile);
              // api call to save the new image with drawing
              const createMarkedImage = new FormData();
              createMarkedImage.append('image', blob);
              try {
                setSaving(true);
                let id = selectedPlatMap;
                if (!id) {
                  const createImage = new FormData();
                  createImage.append('image', originalImageFile);
                  id = await createPlatMap(neighborhoodId, createImage);
                }
                await createMarkedPlatMap(neighborhoodId, id, createMarkedImage);

                enqueueSnackbar('Plat Map Saved!', {
                  variant: 'success'
                });
                fetchPlatMaps();
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? 'Error uploading plat map, please try again.', {
                  variant: 'error'
                });
              } finally {
                setSaving(false);
              }
            }
          }}
        >
          Save
        </SMButton>
        <Button
          type="button"
          variant="contained"
          color="inherit"
          startIcon={<Close />}
          disabled={isSaving}
          onClick={() => {
            history.push('/development/neighborhoods');
          }}
        >
          Cancel
        </Button>
      </ButtonGroup>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  buttonGroup: {
    marginTop: theme.spacing(1)
  },
  logoDrop: {
    marginTop: theme.spacing(1),
    marginBottom: 0
  },
  logoUploaded: {
    margin: theme.spacing(1, 0, 0)
  },
  editButtonsWrapper: {
    margin: theme.spacing(1, 0),
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-around'
  },
  editButtonGroup: {
    flex: 1
  },
  colorLabel: {
    marginRight: '1rem'
  },
  optionsButtonsWrapper: {
    margin: theme.spacing(1, 0),
    display: 'flex',
    alignItems: 'center'
  },
  optionsGroup: {
    display: 'flex',
    backgroundColor: theme.palette.grey[300],
    padding: '.5rem',
    alignItems: 'center'
  },
  removeWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(0.5)
  },
  removeButton: {
    color: theme.palette.error.main
  },
  selectWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '100%'
  },
  select: {
    flex: 1
  }
}));
